/**
 * Manifest schema (Zod). Every harness ships a `harness.manifest.json`
 * next to its source; consumers (harness-loader, drift-scan,
 * harn-service, harn-lab) validate against this schema.
 *
 * Phase 5 of the pi-mono integration plan introduced a v1→v2
 * `schema_version` bump. `ManifestSchema` is a discriminated union;
 * existing v1 manifests on disk continue to parse (both branches
 * preserve the Phase 1-4 field set). v2 adds an optional `agent`
 * block that becomes **required** when `execution.mode === "agent"`
 * (enforced via `.superRefine`). Consumers that need to load both
 * wrap raw JSON in `migrateV1ToV2` (see `./index.ts`) before parsing,
 * so older on-disk manifests never hit a v1-shape parse failure.
 */
import { z } from "zod";
// ⚠ The thinking-level members come from the facade, never retyped here — a
// hand-written copy silently froze at five when upstream grew a sixth
// (lexserver found the same list restated 8× in their package).
import { THINKING_LEVELS } from "@lexiun/lx-llm-core";
export const ScopeSchema = z.enum(["org", "client", "matter", "case"]);
/**
 * Source-assessment gate declaration (Part G / Phase F4;
 * mission-ingest-implementation-plan §13.1(b)/(c)). The per-operation
 * strictness a source-consuming step attaches: `need[]` lists the `.info`
 * fields the operation requires to be filled (e.g. `["role", "weight"]`) and
 * `policy` is the strictness applied when they are not —
 *   - `flag`   — census the gap + continue (non-blocking);
 *   - `prompt` — signal collection (host → `ctx.awaitUser`);
 *   - `block`  — refuse the op until the source is assessed.
 *
 * **DEFINED, not yet ENFORCED.** F4 ships the shape + the `gateInfo` predicate
 * (`@lexiun/harn-sdk` `authoring/source-info.ts`) that evaluates it; the
 * `before_tool_call` runtime enforcement seam is F6. Additive-optional — every
 * existing v1/v2/v3 manifest parses unchanged, no `schema_version` bump.
 */
export const InfoGateSchema = z.object({
    need: z.array(z.string()).default([]),
    policy: z.enum(["flag", "prompt", "block"]),
});
// ── Shared field shapes (v1 + v2 agree on everything outside
// ── schema_version + the v2-only `agent` block) ─────────────────────────────
const SharedManifestFields = {
    id: z.string().min(1),
    version: z.string().min(1),
    scope: ScopeSchema,
    tool: z.object({
        name: z.string().min(1),
        label: z.string().min(1),
        description: z.string().min(1),
        permission_group: z.enum(["READ", "WRITE", "DRAFT"]),
        /**
         * Two-plane access model — the concierge exposure switch.
         * `false` = MISSION-ONLY: the harness is NOT registered as a direct
         * concierge (OpenClaw) tool; it stays reachable only via
         * `lx_delegate` → mission → harn-service. The default is VISIBLE —
         * absent/`undefined` means the harness IS registered as a concierge
         * tool (only an explicit `false` hides it; the consumer checks
         * `=== false`). Additive-optional so existing v1/v2/v3 manifests parse
         * unchanged with no schema_version bump.
         * See ai-docs/lexiun-concierge-agents-tool-access.md (INV-3, INV-10).
         */
        concierge_visible: z.boolean().optional(),
    }),
    input_schema: z.record(z.string(), z.unknown()).optional(),
    output_schema: z.record(z.string(), z.unknown()).optional(),
    /**
     * Ontology knowledge layer (lexiun-ontology-implementation-plan.md §3.1,
     * D-6) — per-harness knowledge preferences. Additive-optional so every
     * existing v1/v2/v3 manifest parses unchanged (no schema_version bump).
     *
     * - `ontology_kernel` — opt-OUT of the host-injected DOMAIN-kernel
     *   system preamble. **Default-on**: absent/`true` ⇒ every LLM call this
     *   harness makes carries the kernel (the host applies
     *   `withSystemPreamble` at the ctx.llm chokepoint). Set **`false`** for
     *   framework/demo harnesses that don't do legal work (`selftest`,
     *   `echo`, `hello-harness`, …) — the consumer checks `=== false`.
     * - `esfera_slice` — opt-IN (P4): when `true` AND the task input carries
     *   a `matterType`, the host appends the matching Tier-2 esfera slice
     *   after the kernel in the same preamble.
     */
    knowledge: z
        .object({
        ontology_kernel: z.boolean().optional(),
        esfera_slice: z.boolean().optional(),
    })
        .optional(),
    execution: z.object({
        mode: z.enum(["in-process", "subprocess", "agent"]),
        entry: z.string().min(1),
    }),
    model: z.object({
        provider: z.string().min(1),
        id: z.string().min(1),
        /**
         * Phase 4 — extended-thinking level.
         *
         * **Enforced since pi-mono Phase 5 follow-up #1 (2026-04-24).**
         * harn-service's `buildLlmClient` propagates this value as the
         * default when the harness's per-call `HarnessLlmParams.reasoning`
         * is undefined — the merged params then reach pi-ai's
         * `streamSimple` / `completeSimple` options. Explicit per-call
         * `params.reasoning` always wins, so harnesses can still override
         * on a call-by-call basis.
         *
         * Provider support varies — Anthropic Opus is the primary target.
         */
        reasoning: z.enum(THINKING_LEVELS).optional(),
    }),
    resources: z
        .object({
        default_timeout_ms: z.number().int().positive().default(120_000),
        max_concurrent: z.number().int().positive().default(2),
        max_input_bytes: z.number().int().positive().default(65_536),
    })
        .default({ default_timeout_ms: 120_000, max_concurrent: 2, max_input_bytes: 65_536 }),
    streaming: z
        .object({
        emits_llm_stream: z.boolean().default(false),
        /**
         * Phase 3 opt-in for `UsageFrame` on the WS wire. Default
         * false → existing manifests never surface usage over the wire
         * (the SDK still computes it and exposes via
         * `StreamRelayResult.usage`; harn-service's WS broadcaster
         * strips UsageFrame unless this flag is true).
         */
        emits_usage: z.boolean().default(false),
    })
        .default({ emits_llm_stream: false, emits_usage: false }),
    agent_await: z.boolean().default(true),
    protocol_version: z.literal(1),
    author: z.string().optional(),
    tags: z.array(z.string()).default([]),
    /**
     * Is a run of this harness the LAWYER'S work, or the platform's own plumbing?
     *
     * A DISPLAY CLASS, deliberately NOT a `tags` entry. `tags` answers a different
     * question — *"which LexIDE widgets should OFFER this harness"*
     * (`lexide/src/lib/harness-tags.ts`) — and loading a second, unrelated question
     * into it means an author editing the array for one purpose silently moves the
     * other. More decisively: `tags` is `z.array(z.string())`, which **cannot
     * fail**, so `"sytem"` would parse clean and silently never filter. A closed
     * enum makes that typo a manifest validation error at load, which is the whole
     * point of declaring the class instead of hardcoding harness ids in the SPA.
     *
     * ⚠ **The default direction is load-bearing: absent ⇒ `user_work` ⇒ VISIBLE.**
     * A real task disappearing from the lawyer's panel because a field failed to
     * plumb is far worse than an infrastructure row a filter missed. Fail-visible,
     * never fail-hidden — asserted on both sides of the seam.
     *
     * Agreed with the `ide` lane 2026-08-06 (bus handshake, thread
     * `20260806T164323Z-ide-tarefas-panel…`): LexIDE filters the default Tarefas
     * view and its tab count on this field, never on `harnessId` and never by
     * parsing a summary string.
     */
    classification: z.enum(["user_work", "infrastructure"]).default("user_work"),
    /**
     * Platform prompt slugs this harness consumes via `ctx.prompts`
     * (lexserver-served Prompt Manager). **Advisory metadata in v1** —
     * surfaced for drift-scan / docs; no registry existence-check gate
     * yet. `required` lists slugs the harness always fetches; `versioned`
     * pins specific slugs to a version/label string when the harness
     * needs a fixed revision rather than `production`.
     */
    prompts: z
        .object({
        required: z.array(z.string()).default([]),
        versioned: z.record(z.string(), z.string()).optional(),
    })
        .optional(),
    /**
     * Baked **lx-utilities** verbs this harness depends on (e.g.
     * `["md2docx"]`). Additive-optional — existing v1+v2 manifests parse
     * unchanged; no `schema_version` bump. Each id must be a member of the
     * generated `@lexiun/lx-utilities` catalog (enforced by the 11th
     * drift-scan check + `harn validate`). This one declaration is *both*
     * the deployability record (the HOT predicate is decidable from it) and,
     * for `mode:agent` harnesses, the set of verbs harn-sdk auto-exposes to
     * the inner LLM as `lx_<id>` tools (opt-out via
     * `agent.policy.exposeUtilities = false`). See ai-docs/lx-utilities-guide.md.
     */
    requires: z
        .object({
        utilities: z.array(z.string()).default([]),
        /**
         * ⚖ Rail (c) of the GENERIC RESOURCE PRELOAD
         * (`planner-reviser-implementation-plan.md` §2.4, ruled by
         * `lexiun-agent-bus-plan` thread …-91hw): the capability is
         * **manifest-DECLARED opt-in per harness — injected context is not free.**
         *
         * When true, the runtime renders the platform's current resources
         * (specialists via `GET /bus/agents` BASIC, the harness registry, the
         * harness-plane tool inventory) ONCE at task setup and hands it to the
         * harness as `ctx.resourcePreload`. Default OFF: a harness that does not
         * choose capability-awareness does not pay for it in tokens.
         *
         * It is a VIEW, never an invocation surface (rail e) — nothing in it may
         * be treated as an address to send work to.
         */
        resourcePreload: z.boolean().default(false),
    })
        .optional(),
    /**
     * Source-assessment gate (Part G / Phase F4; §13.1(b)/(c)). The shape a
     * SOURCE-CONSUMING step declares to require the lawyer's `.info` assessment
     * before relying on an artifact — `need[]` (the `.info` fields it needs
     * filled) + `policy` (`flag` | `prompt` | `block`). Additive-optional —
     * existing manifests parse unchanged, no `schema_version` bump.
     *
     * **DEFINED, not yet ENFORCED at runtime (that's F6).** F4 ships the
     * declaration + the deterministic `gateInfo` predicate that evaluates it.
     */
    info_gate: InfoGateSchema.optional(),
    /**
     * Agent-bus declaration (⚖ D-4, ai-docs/lexiun-agent-bus-plan.md §4.6).
     *
     * Additive-optional shared field — existing manifests parse unchanged and
     * there is deliberately **no `schema_version` bump** (versions 1/2/3 all
     * share these fields; §2.4 of the plan).
     *
     * The manifest IS the auto-registration input: the harness loader
     * registers a declared durable `address` + its `description` with
     * bus-service at scan time, so alias definitions HOT-update with harness
     * deploys rather than needing an image rebuild.
     *
     * - `address` — the durable alias this harness owns (e.g. `kb-manager`).
     *   Omit for a harness that only sends: the runtime then registers a
     *   transient `harness:<rootTaskId>` alias instead. ⚠ EITHER/OR, not both —
     *   a declared address IS the run's identity (so its dormant queue is
     *   drainable); only an address-less harness gets the transient form.
     * - `description` — the capabilities line other agents target on. Required
     *   whenever `address` is set: an alias nobody can understand is an alias
     *   nobody can address, which defeats the directory.
     * - `emits` / `consumes` — declared intent, checked by drift-scan against
     *   what the code actually does.
     */
    bus: z
        .object({
        address: z
            .string()
            .regex(/^[a-z][a-z0-9-]*$/, "bus.address must be a lowercase slug")
            .optional(),
        description: z.string().min(1).max(500).optional(),
        emits: z.array(z.string()).optional(),
        consumes: z.boolean().optional(),
        /**
         * ⚖ STAND-IN DECLARATION — this alias is NOT this harness's own name.
         *
         * Names the harness id this one substitutes for. Required whenever
         * `address !== id`, because that mismatch is the ROOT ENABLER of the
         * impersonation class (⚖ `missions-harnesses`, 2026-08-23): a harness
         * registered under a name that is not its own leaves nothing in the
         * directory pointing back at the manifest whose `tool.description` tells
         * the truth. `kb-manager-stub` advertised a live knowledge base under the
         * PRODUCTION alias `kb-manager` for as long as both shipped, and the only
         * honest surface was three files away.
         *
         * Measured at the ruling: 20 manifests match `address === id`, exactly 1
         * did not, and it was the defective one — so the check fires on the real
         * defect and on nothing else.
         *
         * ⚠ Declaring it obliges a DISCLOSURE MARKER in `bus.description`
         * ("stub" / "stand-in"), enforced by drift-scan — triggered by the
         * DECLARATION, never by parsing prose, which is not mechanically
         * decidable and was deliberately NOT specified.
         */
        standsInFor: z
            .string()
            .regex(/^[a-z][a-z0-9-]*$/, "bus.standsInFor must be a lowercase slug")
            .optional(),
        /**
         * ⚖ SERVABILITY — whether a poller watches this alias's queue and
         * dispatches batched `{mode:"drain"}` runs against it.
         *
         * MOVED here from `specialist.pollerOptIn` (2026-08-23, operator-
         * authorized). It lived in a block whose sibling field `practiceArea`
         * is a **pt-BR legal-practice slug**, so a non-legal service harness
         * had to claim a practice area it does not have in order to be woken.
         * That is a vocabulary defect: being servable is a BUS property, not a
         * legal-domain one.
         *
         * ⚠ Default FALSE, and that is a damper, not a nicety: registration
         * alone must never opt a harness into autonomous ignition (⚖ D-11).
         * Being addressable and being ignitable are different grants — see the
         * a1/a2/b split in `lexiun-agent-bus-plan.md` §10.
         */
        servable: z.boolean().default(false),
    })
        .refine((v) => !v.address || Boolean(v.description), {
        message: "bus.description is required when bus.address is set",
    })
        .refine((v) => !v.servable || Boolean(v.address), {
        message: "bus.servable requires bus.address — a servable harness with no address has no queue for a poller to drain",
    })
        .optional(),
    /**
     * Agent-specialist declaration (⚖ D-12, ruled LEAN 2026-08-19 — slate §9
     * item 9; ai-docs/lexiun-agent-bus-plan.md §P8). Marks this harness as a
     * consultation specialist. Additive-optional shared field, same posture as
     * `bus` above — no `schema_version` bump.
     *
     * Deliberately NO `verbs`, `handshakeRequired`, `tags` or `when_to_use`
     * fields: the operator ruled VERB-LESS v1 (one generic `request`; prose
     * negotiation) and parked the taxonomy/tags in v2 — do not re-add them
     * here without that ruling being reopened.
     *
     * - `domain` — the LONG-PROSE dual-binding capability descriptor
     *   (≤6,000 chars): scope · capabilities · limits · when-to-consult and
     *   when NOT. Forwarded to the bus registry at scan/run registration and
     *   served on SELECTION only (the BASIC/full listing split); the
     *   specialist's own prompt assembly interpolates it from THIS manifest at
     *   run time, so directory and persona cannot skew (one deploy-bound
     *   source).
     * - `practiceArea` — pt-BR grouping slug (e.g. "processo-civil"). GROUPS
     *   the directory; never routes (routing is the `bus.description` row +
     *   `domain` prose, ⚖ prose-not-enums).
     * ⚠ `pollerOptIn` MOVED to `bus.servable` (2026-08-23) and is GONE from
     * here — an ATOMIC move with no dual-read on purpose. A transitional
     * accept-both would be two spellings of one fact, which no drift check can
     * disambiguate (the silent-accept-both hazard, `lexiun-agent-bus-plan.md`
     * §8.0-bis). A manifest still carrying `specialist.pollerOptIn` now FAILS
     * to parse rather than being silently un-servable.
     *
     * Cross-field requirements (enforced in the v2/v3 superRefines): a
     * specialist must run `execution.mode: "agent"` and declare a durable
     * `bus.address` — a consultation agent without an address cannot
     * accumulate the queue the poller exists to drain.
     */
    specialist: z
        .object({
        domain: z.string().min(1).max(6000),
        practiceArea: z
            .string()
            .regex(/^[a-z][a-z0-9-]*$/, "specialist.practiceArea must be a lowercase slug"),
    })
        .strict()
        .optional(),
    /**
     * ⚖ THE EXPERT CLASS (E-1/E-13, operator-stamped 2026-08-23) — a GENERIC-CRAFT
     * servable agent (`xp-*`), as opposed to `specialist{}`'s legal-domain one.
     *
     * ⚠ **NO `practiceArea`, deliberately.** That field is a pt-BR legal-practice
     * slug; an expert has no practice area, and forcing one would be the exact
     * vocabulary defect `bus.servable` was moved off `specialist{}` to escape.
     *
     * ⚖ **O-3 RULED: it reuses the `domain` WIRE field** rather than minting a
     * sibling. `BusRegisterSchema.domain` is a class-agnostic prose slot; a sibling
     * name would force the registrar into `expert?.craft ?? specialist?.domain` —
     * a branch, and two manifest spellings for one directory row.
     *
     * ⛔ **THIS BLOCK GRANTS IDENTITY ONLY.** Declaring it makes `kind:"expert"`
     * true and nothing else: NOT durable, NOT servable, NOT case-scope-exempt.
     * Those stay declared where they were (`durable` on the registration,
     * `bus.servable` on `bus{}`) — see `BusAgentKindSchema`'s note, and do not let
     * a later convenience re-bundle them.
     */
    expert: z
        .object({
        domain: z.string().min(1).max(6000),
    })
        .strict()
        .optional(),
};
/**
 * Shared v2/v3 cross-field check for the `specialist{}` block. One function,
 * two superRefine call sites — the constraint cannot drift between versions.
 */
/**
 * ⚖ A manifest is `specialist{}` XOR `expert{}` — never both.
 *
 * Not a style rule: the registrar derives ONE `kind` from these blocks, so a
 * manifest carrying both would make identity depend on the order of a ternary.
 * Refused at the schema, where it is a compile-time-ish fact rather than a
 * silent precedence.
 */
function refineClassExclusivity(manifest, ctx) {
    if (manifest.specialist && manifest.expert) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expert"],
            message: "a manifest declares specialist{} XOR expert{}, never both — the registrar derives ONE kind from them, so carrying both makes identity depend on a ternary's order.",
        });
    }
}
/**
 * ⚖ An expert, like a specialist, must be agent-mode and durably addressed —
 * a servable agent with no address has no queue for a poller to drain, and a
 * non-agent one is refused pre-dispatch by the `max_iterations` damper anyway.
 */
function refineExpert(manifest, ctx) {
    if (!manifest.expert)
        return;
    if (manifest.execution.mode !== "agent") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expert"],
            message: "expert{} requires execution.mode === 'agent' (⚖ E-4: experts inherit the specialist damper set, which is agent-shaped).",
        });
    }
    if (!manifest.bus?.address) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["bus", "address"],
            message: "expert{} requires a durable bus.address — an expert without an address cannot accumulate the queue a poller drains.",
        });
    }
}
function refineSpecialist(manifest, ctx) {
    if (!manifest.specialist)
        return;
    if (manifest.execution.mode !== "agent") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["specialist"],
            message: "specialist{} requires execution.mode === 'agent' (⚖ D-12: specialists are agent-mode harnesses).",
        });
    }
    if (!manifest.bus?.address) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["bus", "address"],
            message: "specialist{} requires a durable bus.address — a specialist without an address cannot accumulate the queue the poller drains.",
        });
    }
}
/**
 * v2-only `agent.policy` block. Required when `execution.mode` is
 * `"agent"` (refined below) — declares the ceiling on a
 * `streamWithTools` inner loop plus the tool-name allowlist the
 * harness is permitted to dispatch.
 *
 * **Enforced since pi-mono Phase 5 follow-up #1 (2026-04-24).** When
 * `ctx.manifest.agent.policy` is populated (harn-service's executor
 * and harn-lab's `Lab.run` both do this) `streamWithTools`:
 *
 *   1. Clamps the effective iteration cap to
 *      `min(opts.maxIterations, policy.max_iterations)` — a
 *      `tool_iteration_limit_exceeded` error surfaces the lower of
 *      the two in its message.
 *   2. Rejects any declared tool whose `name` is not in
 *      `tools_permitted` (when the array is non-empty) with
 *      `schema_validation_failed` — bails BEFORE the first LLM call,
 *      so the author never pays for a run they'd have to discard.
 *
 * An empty `tools_permitted` array (the schema default) means "no
 * allowlist declared" — enforcement is a no-op so existing v2
 * manifests that don't populate the field keep working.
 */
export const AgentPolicySchema = z.object({
    max_iterations: z.number().int().positive().default(10),
    tools_permitted: z.array(z.string()).default([]),
    /**
     * Opt-OUT of the lx-utilities LLM-tool auto-exposure. **Default-on**:
     * `undefined`/`true` ⇒ harn-sdk auto-generates an `lx_<id>` function-calling
     * tool from the catalog for each verb in `requires.utilities[]` and attaches
     * it to this harness's `streamWithTools` calls. Set **`false`** for a
     * deterministic harness that calls `spawnUtil` only from code but still wants
     * the dependency declared (`exposeUtilities !== false` is the "expose" test).
     * `streamWithRelay` is never affected; raw shell is never exposed.
     * `.optional()` (not `.default(true)`) so existing hand-built `agent.policy`
     * literals stay valid. See ai-docs/lx-utilities-guide.md §6.
     */
    exposeUtilities: z.boolean().optional(),
});
// ── Per-version schemas ─────────────────────────────────────────────────────
export const ManifestV1Schema = z.object({
    schema_version: z.literal(1),
    ...SharedManifestFields,
});
export const ManifestV2Schema = z
    .object({
    schema_version: z.literal(2),
    ...SharedManifestFields,
    /**
     * Phase 5 — agentic policy gate. Optional in general; enforced as
     * **required** via `.superRefine` below when
     * `execution.mode === "agent"`.
     */
    agent: z
        .object({
        policy: AgentPolicySchema,
    })
        .optional(),
})
    .superRefine((manifest, ctx) => {
    if (manifest.execution.mode === "agent" && manifest.agent?.policy === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["agent", "policy"],
            message: "agent.policy is required when execution.mode === 'agent' (Phase 5).",
        });
    }
    refineSpecialist(manifest, ctx);
    refineExpert(manifest, ctx);
    refineClassExclusivity(manifest, ctx);
});
// ── v3 — Phase 5.C of the lx-llm-resolver migration ─────────────────────────
// Same shape as v2 EXCEPT the `model` field is a profile-name string
// (e.g. "MED") that the host resolves to a (provider, model) pair via
// `@lexiun/lx-llm-resolver`. v1/v2 manifests with the legacy `{provider,
// id, reasoning?}` object continue to load (no auto-migration); the
// loader's `resolveManifestModel` helper bridges both shapes.
// Destructure-without-`model` so the v3 spread doesn't carry the
// legacy object shape from SharedManifestFields. Type assertion: the
// destructure pattern is opaque to TS's `as const` tracking.
const { model: _legacyModel, ...sharedFieldsWithoutModel } = SharedManifestFields;
void _legacyModel;
export const ManifestV3Schema = z
    .object({
    schema_version: z.literal(3),
    ...sharedFieldsWithoutModel,
    /**
     * Profile-name string. The host's lx-llm-resolver picks the
     * concrete (provider, model) at run time, scoped to the org's
     * apiKeys. Built-in: "MAX" / "MED" / "LOW". Custom profile names
     * resolve only if the host's resolver has them registered.
     *
     * The optional `reasoning` from the v1/v2 object form is replaced
     * by the resolver's profile policy (`policy.thinkingDefault`).
     */
    model: z.string().min(1),
    agent: z
        .object({
        policy: AgentPolicySchema,
    })
        .optional(),
})
    .superRefine((manifest, ctx) => {
    if (manifest.execution.mode === "agent" && manifest.agent?.policy === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["agent", "policy"],
            message: "agent.policy is required when execution.mode === 'agent'.",
        });
    }
    refineSpecialist(manifest, ctx);
    refineExpert(manifest, ctx);
    refineClassExclusivity(manifest, ctx);
});
// ── Exported discriminated union + types ────────────────────────────────────
export const ManifestSchema = z.discriminatedUnion("schema_version", [
    ManifestV1Schema,
    ManifestV2Schema,
    ManifestV3Schema,
]);
//# sourceMappingURL=schema.js.map